import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Wallet, Check, AlertCircle, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { usersRepository } from '@/repositories/users.repository'
import { useToast } from '@/hooks/use-toast'

interface WalletAddressModalProps {
  isOpen: boolean
  onClose: () => void
  currentAddress?: string
}

export function WalletAddressModal({ isOpen, onClose, currentAddress }: WalletAddressModalProps) {
  const [walletAddress, setWalletAddress] = useState(currentAddress || '')
  const { toast } = useToast()
  const qc = useQueryClient()

  const linkWalletMutation = useMutation({
    mutationFn: (address: string) => usersRepository.linkCryptoWallet(address),
    onSuccess: () => {
      toast({
        title: 'Wallet Linked Successfully',
        description: 'Your wallet address has been saved.',
        variant: 'default',
      })
      qc.invalidateQueries({ queryKey: ['user'] })
      onClose()
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to Link Wallet',
        description: error?.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!walletAddress.trim()) return
    linkWalletMutation.mutate(walletAddress.trim())
  }

  const isValidAddress = walletAddress.trim().length >= 20

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-card to-muted/30 border-0 shadow-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Wallet className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-xl">
              {currentAddress ? 'Update Wallet Address' : 'Link Wallet Address'}
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            {currentAddress 
              ? 'Update your crypto wallet address for withdrawals.' 
              : 'Link your crypto wallet address to enable withdrawals and receive payments.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Wallet Address (TRC20/BEP20)
            </label>
            <div className="relative">
              <Input
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="Enter your wallet address..."
                className="pr-10 bg-muted/50 border-muted-foreground/20 focus:border-primary"
                disabled={linkWalletMutation.isPending}
              />
              <AnimatePresence>
                {isValidAddress && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <Check className="h-5 w-5 text-green-500" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <p className="text-xs text-muted-foreground">
              Supported networks: TRC20 (TRON), BEP20 (Binance Smart Chain)
            </p>
          </div>

          {currentAddress && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
              <p className="text-xs text-yellow-600 dark:text-yellow-400">
                Changing your wallet address will affect future withdrawals. Make sure the new address is correct.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={linkWalletMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={!isValidAddress || linkWalletMutation.isPending}
            >
              {linkWalletMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : currentAddress ? (
                'Update Address'
              ) : (
                'Link Wallet'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}